// packages/web/src/store/api/baseQueryWithReauth.ts
import { fetchBaseQuery, FetchArgs, BaseQueryFn } from '@reduxjs/toolkit/query/react';

import { getApiBaseUrl } from '../../utils/apiBase';

// ----- one-time debug so we can see what base the web will use in harness/dev
try {
  const dbg = getApiBaseUrl();
  if (process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.DEBUG_MODE === 'true') {
    // eslint-disable-next-line no-console
    console.log('[RTK][baseQuery:init] getApiBaseUrl() =', dbg);
  }
} catch {}


// ────────────────────────────────────────────────────────────────
// E2E / Harness net tracing + correlation
// ────────────────────────────────────────────────────────────────
const TRACE_NET =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_E2E_NETLOGS === '1') ||
  (typeof process !== 'undefined' && process.env?.SMOKE_HARNESS_WEEK === 'Week 11');
function genCid() {
  try { return Math.random().toString(36).slice(2, 10); } catch { return String(Date.now()); }
}
// Only emit trace/correlation headers on Week 11 or when explicitly enabled
const ALLOW_TRACE_DEFAULT = !!TRACE_NET;

// Minimal store shape we read (avoid circular deps on SSR)
type MinimalRootState = {
  auth?: { token?: string | null; user?: any | null };
  tenant?: { id?: string | null; subdomain?: string | null };
};

// --- helpers ---------------------------------------------------------------

// Strict UUID detector so we never send non-UUIDs (like "t1") as X-Tenant-Id
const UUID_RE =
  /^[0-9a-f-]{8}-[0-9a-f-]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (v?: string | null): v is string => !!v && UUID_RE.test(v);

// Looks like a JWT if it has exactly three dot-separated segments (header.payload.signature)
const isLikelyJwt = (t?: string | null): t is string =>
  !!t &&
  t.split('.').length === 3 &&
  t.split('.')[0].length > 0 &&
  t.split('.')[1].length > 0 &&
  t.split('.')[2].length > 0;


// Safe base64 decode in browser or Node
function safeAtob(b64: string): string {
  try {
    // @ts-ignore - atob present in browsers
    if (typeof atob === 'function') return atob(b64);
  } catch {}
  try {
    return Buffer.from(b64, 'base64').toString('utf8');
  } catch {
    return '';
  }
}

// Base64URL → Base64 (JWT uses URL-safe Base64 without padding)
function b64UrlToB64(seg: string): string {
  try {
    let s = String(seg || '').replace(/-/g, '+').replace(/_/g, '/');
    const pad = s.length % 4;
    if (pad) s += '='.repeat(4 - pad);
    return s;
  } catch {
    return '';
  }
}


const isStubToken = (t?: string | null) =>
  !!t && t.split('.').length === 3 && t.split('.')[2] === 'stub';

function decodeTenantId(token: string | null): string | null {
  if (!isLikelyJwt(token)) return null;
  try {
    const seg = (token as string).split('.')[1];
    const payload = JSON.parse(safeAtob(b64UrlToB64(seg)));
    const tid = payload.tenantId || payload.tenant_id || null;
    return typeof tid === 'string' ? tid : null;
  } catch {
    return null;
  }
}

function decodeSubdomain(token: string | null): string | null {
  if (!isLikelyJwt(token)) return null;
  try {
    const seg = (token as string).split('.')[1];
    const payload = JSON.parse(safeAtob(b64UrlToB64(seg)));
    const sub = (payload?.subdomain ?? '').toString().trim();
    return sub || null;
  } catch {
    return null;
  }
}

// Prefer a token that actually carries a tenantId claim (real JWT) over a stub
function chooseBestToken(
  stateToken: string | null,
  storageToken: string | null
): string | null {
  const stateTenant = decodeTenantId(stateToken);
  const storageTenant = decodeTenantId(storageToken);
  if (isUuid(stateTenant)) return stateToken;
  if (isUuid(storageTenant)) return storageToken;
  return stateToken || storageToken;
}

function readTokenFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const ls =
      window.localStorage.getItem('token') ||
      window.localStorage.getItem('faang_token');
    if (ls) return ls;
    // Accept both legacy "token" and current "faang_token"
    const m1 = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
    const m2 = document.cookie.match(/(?:^|;\s*)faang_token=([^;]+)/);
    return m1 ? decodeURIComponent(m1[1]) : (m2 ? decodeURIComponent(m2[1]) : null);
  } catch {
    return null;
  }
}

function isWeek11(): boolean {
  // Week 11 only. Do NOT key off NEXT_PUBLIC_SMOKE_BANNER
  // to avoid non-W11 regressions (e.g., Week 5).
  return process.env.SMOKE_HARNESS_WEEK === 'Week 11';
}

function isHarnessWeek(): boolean {
  try {
    const w = String(process.env.SMOKE_HARNESS_WEEK || '');
    return /^(Week 5|Week 6|Week 11)$/i.test(w);
  } catch {
    return false;
  }
}

function currentPath(): string {
  try {
    return typeof window !== 'undefined' ? window.location.pathname : '(ssr)';
  } catch {
    return '(unknown)';
  }
}

// --- base query ------------------------------------------------------------

// Fetch without a fixed base; we will absolutize per request
const rawBaseQuery = fetchBaseQuery({
  baseUrl: '',
  credentials: 'include',
  prepareHeaders: (headers, api) => {
    try {
      const getSafeState = (): Partial<MinimalRootState> | undefined => {
        try {
          const s = api?.getState?.();
          if (s) return s as Partial<MinimalRootState>;
        } catch {}
        if (typeof window !== 'undefined') {
          try {
            return (window as any).store?.getState?.() as Partial<MinimalRootState> | undefined;
          } catch {}
        }
        return undefined;
      };

      const state = getSafeState();

      // Mark harness traffic only for Week 11 (avoid adding custom headers on Week 5/6 to keep CORS surface minimal)
      if (isWeek11()) {
        headers.set('x-smoke-harness', 'true');
      }

      // Correlation + E2E toggle (header emission restricted to Week 11 or explicit net tracing)
      const allowTrace = ALLOW_TRACE_DEFAULT || isWeek11();
      if (allowTrace) {
        if (!(headers as any).has?.('X-CID')) headers.set('X-CID', genCid());
        if (TRACE_NET && !(headers as any).has?.('X-E2E-TRACE')) headers.set('X-E2E-TRACE', '1');
      }

      // Gather token candidates
      let reduxToken = (state as any)?.auth?.token ?? null;
      let cookieToken: string | null = null;
      let lsToken: string | null = null;

      if (typeof window !== 'undefined') {
        try {
          // Accept either "token" or "faang_token"
          const m1 = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
          const m2 = document.cookie.match(/(?:^|;\s*)faang_token=([^;]+)/);
          if (m1) cookieToken = decodeURIComponent(m1[1]);
          else if (m2) cookieToken = decodeURIComponent(m2[1]);
        } catch {}
        // Accept either localStorage "token" or "faang_token"
        try {
          lsToken =
            window.localStorage.getItem('token') ||
            window.localStorage.getItem('faang_token');
        } catch {}
      }

      // Pick the strongest token (has a tenantId claim). This avoids using the Week-11 stub
      // when a fresh post-register JWT exists in localStorage.
      let token = chooseBestToken(reduxToken || cookieToken, lsToken || cookieToken);
      // Only drop **stub** tokens in Week 11. If a token lacks a tenantId claim,
      // keep it and rely on X-Subdomain routing (API supports this).
      const tid = decodeTenantId(token);
      const IS_W11_HDR = isWeek11();
      if (isStubToken(token) && IS_W11_HDR) {
        try {
          if ((process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.DEBUG_MODE === 'true') && token) {
            // eslint-disable-next-line no-console
            console.warn('[baseQueryWithReauth] dropping stub token (Week 11)');
          }
        } catch {}
        token = null;
      }

      // Authorization (only if we actually have a token)
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');

      // Subdomain: Redux -> sessionStorage -> localStorage -> JWT -> query -> host prefix
      let sub: string | null =
        (state as any)?.tenant?.subdomain ??
        (typeof window !== 'undefined'
          ? window.sessionStorage.getItem('subdomain') ||
            window.localStorage.getItem('subdomain') ||
            null
          : null);

      // If still missing, lift it off the JWT (common right after register)
      if (!sub) {
        const fromJwt = decodeSubdomain(token);
        if (fromJwt) sub = fromJwt;
      }
      // Prefer the JWT claim when present to avoid header/JWT drift
      const jwtSub = decodeSubdomain(token);
      if (jwtSub) sub = jwtSub;
      
      if (!sub && typeof window !== 'undefined') {
        try {
          const params = new URLSearchParams(window.location.search);
          sub = (params.get('subdomain') || params.get('tenant')) as string | null;
        } catch {}
        // Host-derived subdomain (handles common 3-label hosts: foo.example.com)
        if (!sub) {
          const host = window.location.hostname || '';
          const parts = host.split('.');
          // prefer non-"www" first label when there are >= 3 labels (foo.example.com)
          if (parts.length >= 3 && parts[0].toLowerCase() !== 'www') {
            // Special case: don't treat main production domains as subdomains
            // visionarynow.com, project2.visionarynow.com, etc. should not send subdomain
            if (host.includes('visionarynow.com')) {
              sub = null; // Don't set subdomain for visionarynow.com domains
            } else {
              sub = parts[0] || null;
            }
          }
        }
      }

      // TenantId: tenant slice -> user -> JWT -> localStorage (UUID only)
      const tenantId =
        (state as any)?.tenant?.id ??
        (state as any)?.auth?.user?.tenantId ??
        decodeTenantId(token) ??
        (typeof window !== 'undefined' ? window.localStorage.getItem('tenantId') : null);

      const haveTid = isUuid(tenantId);
      if (haveTid) {
        headers.set('x-tenant-id', tenantId!);
      } else if (sub) {
        headers.set('x-subdomain', sub);
      }

      // Low-noise auth-source breadcrumbs (guarded)
      try {
        const DBG =
          process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.DEBUG_MODE === 'true';
        if (DBG) {
          // eslint-disable-next-line no-console
          console.log('[baseQueryWithReauth][auth-src]', {
            redux: !!reduxToken,
            lsToken: !!lsToken,
            cookieToken: !!cookieToken,
            attachedAuth: !!token,
            tid: isUuid(tid) ? tid : null,
            usedHeader: haveTid ? 'x-tenant-id' : (sub ? 'x-subdomain' : '(none)'),
            subdomain: sub || null,
            week: process.env.SMOKE_HARNESS_WEEK || ''
          });
        }
      } catch {}
      // Do not force Content-Type; callers with body set it.
    } catch (e) {
      // Keep quiet in tests
      // eslint-disable-next-line no-console
      console.warn('[baseQueryWithReauth] prepareHeaders error (ignored)', e);
    }
    return headers;
  },
});

// Make absolute URL from a path or url, using runtime base.
// Browser prod/stage → '/api/...'(Next rewrite to API)
// Browser dev (UI 3001) → 'http(s)://host:3000/api/...'
// SSR → absolute using env (NEXT_PUBLIC_API_URL/API_BASE or localhost:3000)
function absolutizeUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

  // normalize '/api/foo' vs 'foo'
  let rel = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  rel = rel.replace(/^\/api\//, '/');

  const base = getApiBaseUrl(); // can be '/api' or absolute ending with /api

  if (typeof window !== 'undefined') {
    if (/^https?:\/\//i.test(base)) {
      const b = base.replace(/\/+$/, '');
      return `${b}${rel.startsWith('/') ? '' : '/'}${rel}`;
    }
    // If running locally (Next on :3011), always target API on :3010 (avoids Next /api catch)
    try {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        const abs = `http://${host}:3010/api`.replace(/\/+$/, '');
        return `${abs}${rel.startsWith('/') ? '' : '/'}${rel}`;
      }
    } catch {}
    // Otherwise, use relative '/api' (only safe if an external proxy routes it to the backend)
    return `/api${rel}`;
  }

  // SSR absolute fallback
  // Always ensure an absolute base that ends with '/api' on the server to avoid routing mismatches.
  let envAbs = (process.env.NEXT_PUBLIC_API_URL || process.env.API_BASE || 'http://localhost:3010/api') as string;
  if (!/^https?:\/\//i.test(envAbs)) {
    envAbs = 'http://localhost:3010/api';
  }
  envAbs = envAbs.replace(/\/+$/, '');
  if (!/\/api$/i.test(envAbs)) {
    envAbs = `${envAbs}/api`.replace(/\/+$/, '');
  }
  return `${envAbs}${rel.startsWith('/') ? '' : '/'}${rel}`;
}

// --- exported base query ----------------------------------------------------

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  unknown
> = async (args, api, extraOptions) => {
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
  const originalUrl = typeof args === 'string' ? args : (args.url || '');
  const IS_W11 = isWeek11();
const allowTrace = ALLOW_TRACE_DEFAULT || IS_W11;

  const normalizedUrl = originalUrl.replace(/^\/api\//, '/');
  const finalUrl = absolutizeUrl(normalizedUrl);

  const start = Date.now();
  const cidLocal = genCid();
  // Ensure X-CID / X-E2E-TRACE are present so API logs correlate with client logs
  let argObj: FetchArgs = typeof args === 'string' ? { url: finalUrl, method } : { ...args, url: finalUrl };
  const hdr = new Headers((argObj as any).headers || {});
  if (allowTrace) {
    if (!hdr.has('X-CID')) hdr.set('X-CID', cidLocal);
    if (TRACE_NET && !hdr.has('X-E2E-TRACE')) hdr.set('X-E2E-TRACE', '1');
  }
  argObj = { ...argObj, headers: hdr };
  if (allowTrace && TRACE_NET) {
    try { console.error('[NET]', JSON.stringify({ stage: 'start', cid: cidLocal, method: String(method).toUpperCase(), url: normalizedUrl })); } catch {}
  }

  const DEBUG_NOISY =
    TRACE_NET || process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' || process.env.DEBUG_MODE === 'true';
  // Extra diagnostics (gated) for customer/service endpoints
  if (DEBUG_NOISY && (normalizedUrl.includes('/customers') || normalizedUrl.includes('/services'))) {

    const state = api.getState() as MinimalRootState;
    // eslint-disable-next-line no-console
    console.log('[rtkQuery] request', method, normalizedUrl, {
      token: state.auth?.token,
      subdomain: state.tenant?.subdomain,
      tenantId:
        state.tenant?.id ||
        state.auth?.user?.tenantId ||
        decodeTenantId(state.auth?.token ?? null),
    });
  }

  const normalizedArgs = argObj;

  const result = await rawBaseQuery(normalizedArgs, api, extraOptions);
  const duration = Date.now() - start;

  const status = (result as any)?.meta?.response?.status;
  const headers = (result as any)?.meta?.response?.headers;
  const sessionId = headers?.get?.('x-session-id');
  if (sessionId && typeof window !== 'undefined') {
    window.sessionStorage.setItem('session-id', sessionId);
  }

  const expired = headers?.get?.('x-session-expired');
  if (expired === 'true') {
    // Drop session id + auth slice
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('session-id');
    }
    api.dispatch({ type: 'auth/logout' });

    // On Week 11, NEVER navigate — tests own the auth surface
    if (!IS_W11) {
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/auth/')
      ) {
        // eslint-disable-next-line no-console
        console.info('[W11-TRACE] nav->/login from baseQueryWithReauth.ts:expired', {
          path: currentPath(),
          reason: 'session-expired',
        });
        window.location.href = '/login';
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('[baseQueryWithReauth] suppressed redirect on x-session-expired (Week 11).');
    }
    return result;
  }

  // Compact, greppable end/error lines
  if ('error' in result && (result as any).error) {
    const statusErr = (result as any)?.meta?.response?.status ?? (result as any).error?.status ?? 0;
    if (TRACE_NET) {
      try { console.error('[NET]', JSON.stringify({ stage: 'error', cid: cidLocal, method: String(method).toUpperCase(), url: normalizedUrl, status: statusErr, durationMs: duration })); } catch {}
    }
  } else {
    const statusOk = (result as any)?.meta?.response?.status ?? 200;
    if (allowTrace && TRACE_NET) {
      try { console.error('[NET]', JSON.stringify({ stage: 'end', cid: cidLocal, method: String(method).toUpperCase(), url: normalizedUrl, status: statusOk, durationMs: duration })); } catch {}
    }
  }

  if (DEBUG_NOISY && (normalizedUrl.includes('/customers') || normalizedUrl.includes('/services'))) {
    let size = 0;
    try {
      const text = JSON.stringify((result as any).data || '');
      size = text.length;
    } catch {
      size = 0;
    }
    const state = api.getState() as MinimalRootState;
    // eslint-disable-next-line no-console
    console.log('[rtkQuery] response', {
      method,
      url: normalizedUrl,
      status,
      size,
      result,
      token: state.auth?.token,
      subdomain: state.tenant?.subdomain,
      tenantId:
        state.tenant?.id ||
        state.auth?.user?.tenantId ||
        decodeTenantId(state.auth?.token ?? null),
    });
  }

  if ((result as any).error) {
    if (status === 401 || status === 403) {
      const state = api.getState() as MinimalRootState;
      const reduxToken = (state as any)?.auth?.token || null;
      const storageToken = readTokenFromStorage();
      const hasAnyToken = !!(reduxToken || storageToken);

      if (!hasAnyToken) {
        api.dispatch({ type: 'auth/logout' });

        // On Week 11, never navigate away; tests own the auth surface
        if (!IS_W11) {
          if (
            typeof window !== 'undefined' &&
            !window.location.pathname.startsWith('/auth/')
          ) {
            // eslint-disable-next-line no-console
            console.info('[W11-TRACE] nav->/login from baseQueryWithReauth.ts:noToken', {
              path: currentPath(),
              reason: 'noToken',
            });
            window.location.href = '/login';
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[baseQueryWithReauth] suppressed redirect on 401/403 (Week 11).');
        }
      }
      // else: token present but API returned 401/403 — let caller handle UI
    }
  }

  return result;
};
